/**
 * CMS SDK - Context Provider
 * 内容管理系统的状态管理
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { cmsApi } from '../services/cmsApi';
import { setLanguage, getLanguage } from '../i18n';

// 创建 Context
const CMSContext = createContext(null);

/**
 * CMS Provider 组件
 * @param {Object} props - 组件属性
 * @param {ReactNode} props.children - 子组件
 * @param {string} props.apiUrl - API 基础 URL
 * @param {string} props.token - 认证 Token
 * @param {string} props.language - 初始语言
 * @param {Function} props.onError - 错误处理回调
 */
export function CMSProvider({ 
  children, 
  apiUrl, 
  token, 
  language = 'zh',
  onError 
}) {
  // 状态
  const [contents, setContents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [currentContent, setCurrentContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0,
  });

  // 初始化配置
  useEffect(() => {
    if (apiUrl) {
      cmsApi.setBaseUrl(apiUrl);
    }
    if (token) {
      cmsApi.setToken(token);
    }
    if (language) {
      setLanguage(language);
    }
  }, [apiUrl, token, language]);

  // 错误处理
  const handleError = useCallback((err) => {
    setError(err.message);
    if (onError) {
      onError(err);
    }
  }, [onError]);

  // ============ 内容操作 ============

  /**
   * 加载内容列表
   */
  const loadContents = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await cmsApi.getContents({
        page: pagination.page,
        page_size: pagination.pageSize,
        ...params,
      });
      setContents(result.items || result.data || []);
      setPagination(prev => ({
        ...prev,
        total: result.total || 0,
        totalPages: result.total_pages || Math.ceil((result.total || 0) / prev.pageSize),
      }));
      return result;
    } catch (err) {
      handleError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, handleError]);

  /**
   * 加载单个内容
   */
  const loadContent = useCallback(async (contentId) => {
    setLoading(true);
    setError(null);
    try {
      const result = await cmsApi.getContent(contentId);
      setCurrentContent(result);
      return result;
    } catch (err) {
      handleError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  /**
   * 创建内容
   */
  const createContent = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    try {
      const result = await cmsApi.createContent(data);
      setContents(prev => [result, ...prev]);
      return result;
    } catch (err) {
      handleError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  /**
   * 更新内容
   */
  const updateContent = useCallback(async (contentId, data) => {
    setLoading(true);
    setError(null);
    try {
      const result = await cmsApi.updateContent(contentId, data);
      setContents(prev => prev.map(c => c.id === contentId ? result : c));
      if (currentContent?.id === contentId) {
        setCurrentContent(result);
      }
      return result;
    } catch (err) {
      handleError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentContent, handleError]);

  /**
   * 删除内容
   */
  const deleteContent = useCallback(async (contentId) => {
    setLoading(true);
    setError(null);
    try {
      await cmsApi.deleteContent(contentId);
      setContents(prev => prev.filter(c => c.id !== contentId));
      if (currentContent?.id === contentId) {
        setCurrentContent(null);
      }
    } catch (err) {
      handleError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentContent, handleError]);

  /**
   * 发布内容
   */
  const publishContent = useCallback(async (contentId) => {
    setLoading(true);
    setError(null);
    try {
      const result = await cmsApi.publishContent(contentId);
      setContents(prev => prev.map(c => c.id === contentId ? result : c));
      if (currentContent?.id === contentId) {
        setCurrentContent(result);
      }
      return result;
    } catch (err) {
      handleError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentContent, handleError]);

  /**
   * 取消发布
   */
  const unpublishContent = useCallback(async (contentId) => {
    setLoading(true);
    setError(null);
    try {
      const result = await cmsApi.unpublishContent(contentId);
      setContents(prev => prev.map(c => c.id === contentId ? result : c));
      if (currentContent?.id === contentId) {
        setCurrentContent(result);
      }
      return result;
    } catch (err) {
      handleError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentContent, handleError]);

  // ============ 分类操作 ============

  /**
   * 加载分类
   */
  const loadCategories = useCallback(async () => {
    try {
      const result = await cmsApi.getCategories();
      setCategories(result.items || result.data || result || []);
      return result;
    } catch (err) {
      handleError(err);
      throw err;
    }
  }, [handleError]);

  /**
   * 创建分类
   */
  const createCategory = useCallback(async (data) => {
    try {
      const result = await cmsApi.createCategory(data);
      setCategories(prev => [...prev, result]);
      return result;
    } catch (err) {
      handleError(err);
      throw err;
    }
  }, [handleError]);

  /**
   * 更新分类
   */
  const updateCategory = useCallback(async (categoryId, data) => {
    try {
      const result = await cmsApi.updateCategory(categoryId, data);
      setCategories(prev => prev.map(c => c.id === categoryId ? result : c));
      return result;
    } catch (err) {
      handleError(err);
      throw err;
    }
  }, [handleError]);

  /**
   * 删除分类
   */
  const deleteCategory = useCallback(async (categoryId) => {
    try {
      await cmsApi.deleteCategory(categoryId);
      setCategories(prev => prev.filter(c => c.id !== categoryId));
    } catch (err) {
      handleError(err);
      throw err;
    }
  }, [handleError]);

  // ============ 标签操作 ============

  /**
   * 加载标签
   */
  const loadTags = useCallback(async () => {
    try {
      const result = await cmsApi.getTags();
      setTags(result.items || result.data || result || []);
      return result;
    } catch (err) {
      handleError(err);
      throw err;
    }
  }, [handleError]);

  /**
   * 创建标签
   */
  const createTag = useCallback(async (data) => {
    try {
      const result = await cmsApi.createTag(data);
      setTags(prev => [...prev, result]);
      return result;
    } catch (err) {
      handleError(err);
      throw err;
    }
  }, [handleError]);

  /**
   * 删除标签
   */
  const deleteTag = useCallback(async (tagId) => {
    try {
      await cmsApi.deleteTag(tagId);
      setTags(prev => prev.filter(t => t.id !== tagId));
    } catch (err) {
      handleError(err);
      throw err;
    }
  }, [handleError]);

  // ============ 分页操作 ============

  const setPage = useCallback((page) => {
    setPagination(prev => ({ ...prev, page }));
  }, []);

  const setPageSize = useCallback((pageSize) => {
    setPagination(prev => ({ ...prev, pageSize, page: 1 }));
  }, []);

  // Context 值
  const value = {
    // 状态
    contents,
    categories,
    tags,
    currentContent,
    loading,
    error,
    pagination,
    
    // 内容操作
    loadContents,
    loadContent,
    createContent,
    updateContent,
    deleteContent,
    publishContent,
    unpublishContent,
    setCurrentContent,
    
    // 分类操作
    loadCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    
    // 标签操作
    loadTags,
    createTag,
    deleteTag,
    
    // 分页操作
    setPage,
    setPageSize,
    
    // API 实例
    api: cmsApi,
  };

  return (
    <CMSContext.Provider value={value}>
      {children}
    </CMSContext.Provider>
  );
}

/**
 * 使用 CMS Context 的 Hook
 * @returns {Object} CMS Context 值
 */
export function useCMS() {
  const context = useContext(CMSContext);
  if (!context) {
    throw new Error('useCMS must be used within a CMSProvider');
  }
  return context;
}

export default CMSContext;
